import { Badge, Button, CloseButton, Group, Image, Modal, Stack, Text } from "@mantine/core";
import { visualsFor } from "./categories";
import { formatSentAt } from "./time";
import type { PortalAnnouncement } from "./types";

/** Regular ``fullscreen`` announcement rendered as a **centered modal that's
 *  part of the page** (the page dims behind it) — distinct from the
 *  edge-to-edge ``fullscreen_takeover`` (see ``AnnouncementFullscreen``),
 *  which is reserved for evacuations / critical alerts.
 *
 *  The image (when present) is a contained hero at the top of the card, not a
 *  dimmed full-bleed backdrop, so a designed poster reads cleanly without the
 *  text chrome fighting it. Closing is dismissal — the caller decides whether
 *  that's permanent or session-only, but NOT whether it is possible.
 *
 *  ``onClose`` is REQUIRED (#341). It used to be optional with a no-op
 *  fallback, which — together with ``withCloseButton={false}`` — left the modal
 *  with no X, no Dismiss button, and inert Escape / click-outside: the only way
 *  past it was a page reload, and a kiosk may have no way to ask for one. An
 *  announcement that must be acknowledged elsewhere is expressed by what the
 *  handler does, not by removing every exit. */
export function AnnouncementModal({
  announcement,
  onClose,
}: {
  announcement: PortalAnnouncement;
  onClose: () => void;
}) {
  const visuals = visualsFor(announcement.category);
  const message = announcement.message?.trim();
  const image = announcement.image;
  // Mantine colour from the CATEGORY too, so the badge/border/icon agree
  // with the background every other surface paints (#327).
  const color = visuals.mantineColor;

  return (
    <Modal
      opened
      onClose={onClose}
      centered
      size="lg"
      radius="md"
      padding={0}
      withCloseButton={false}
      overlayProps={{ backgroundOpacity: 0.55, blur: 2 }}
      aria-label={announcement.title || visuals.label}
    >
      {image?.url && (
        <Image
          src={image.url}
          alt={image.alt_text || ""}
          h={220}
          fit={image.object_fit === "contain" ? "contain" : "cover"}
        />
      )}
      <Stack gap="sm" p="lg">
        <Group justify="space-between" wrap="nowrap">
          <Badge color={color} variant="light" radius="sm">
            {visuals.icon} {visuals.label}
            {announcement.is_site_wide ? " · Site-wide" : ""}
          </Badge>
          {/* Not Mantine's own header close button: ``padding={0}`` puts that
              header above the hero image, where it overlays the poster. This
              one sits on the card's first text row, opposite the badge. */}
          <CloseButton aria-label="Dismiss announcement" onClick={onClose} />
        </Group>
        <Text fz="xl" fw={700}>
          {announcement.title?.trim() || visuals.label}
        </Text>
        {message && (
          <Text c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
            {message}
          </Text>
        )}
        {formatSentAt(announcement.raised_at) && (
          <Text fz="xs" c="dimmed">
            Sent {formatSentAt(announcement.raised_at)}
          </Text>
        )}
        <Group justify="flex-end" mt="xs">
          <Button onClick={onClose}>Dismiss</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
